import { ReactNode, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Atom,
  Facebook,
  Linkedin,
  LogOut,
  Twitter,
  User,
  Youtube,
} from "lucide-react";
import { useAuthStore } from "@/stores/authStore";

const SOCIAL_LINKS = [
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/fission-labs",
    icon: Linkedin,
  },
  {
    label: "YouTube",
    href: "https://www.youtube.com/channel/UCElcmWqK5ug_9JcENeVVL2A",
    icon: Youtube,
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/fissionlabs",
    icon: Facebook,
  },
  {
    label: "Twitter",
    href: "https://twitter.com/fissionlabs",
    icon: Twitter,
  },
] as const;

const BRAND_COLOR = "#F4A582";

export function PublicHeader() {
  const { user, signOut } = useAuthStore();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-gray-100 bg-white">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 lg:px-10">
        <a
          href="https://www.fissionlabs.com"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 shrink-0"
        >
          <span
            className="text-xl font-bold tracking-[0.08em] sm:text-2xl"
            style={{ color: BRAND_COLOR }}
          >
            FISSION
          </span>
          <Atom className="h-7 w-7 text-[#4A76A8] sm:h-8 sm:w-8" strokeWidth={1.75} />
          <span
            className="text-xl font-bold tracking-[0.08em] sm:text-2xl"
            style={{ color: BRAND_COLOR }}
          >
            LABS
          </span>
        </a>

        <div className="flex items-center gap-3 sm:gap-4">
          <div className="hidden sm:flex items-center gap-2.5">
            {SOCIAL_LINKS.map(({ label, href, icon: Icon }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-black text-white transition-opacity hover:opacity-80"
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>

          <div className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              aria-label="Open menu"
              aria-expanded={menuOpen}
              className="flex h-8 w-8 flex-col items-end justify-center gap-1.5 p-1"
            >
              <span className="block h-0.5 w-7 bg-black" />
              <span className="block h-0.5 w-5 bg-black" />
            </button>

            {menuOpen ? (
              <>
                <button
                  type="button"
                  aria-label="Close menu"
                  className="fixed inset-0 z-40 cursor-default"
                  onClick={() => setMenuOpen(false)}
                />
                <div className="absolute right-0 top-full z-50 mt-3 min-w-[220px] rounded-xl border border-gray-100 bg-white py-2 shadow-xl">
                  <div className="border-b border-gray-100 px-4 py-3 sm:hidden">
                    <div className="flex items-center gap-2">
                      {SOCIAL_LINKS.map(({ label, href, icon: Icon }) => (
                        <a
                          key={label}
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={label}
                          onClick={() => setMenuOpen(false)}
                          className="flex h-8 w-8 items-center justify-center rounded-full bg-black text-white transition-opacity hover:opacity-80"
                        >
                          <Icon className="h-4 w-4" />
                        </a>
                      ))}
                    </div>
                  </div>

                  {user ? (
                    <>
                      <div className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600">
                        <User className="h-4 w-4" />
                        <span className="truncate">
                          {user.displayName || user.username || user.email}
                        </span>
                      </div>
                      <Link
                        to="/dashboard"
                        onClick={() => setMenuOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-800 hover:bg-gray-50"
                      >
                        Dashboard
                      </Link>
                      <button
                        type="button"
                        onClick={() => {
                          setMenuOpen(false);
                          void signOut();
                        }}
                        className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-800 hover:bg-gray-50"
                      >
                        <LogOut className="h-4 w-4" />
                        Sign Out
                      </button>
                    </>
                  ) : (
                    <Link
                      to="/auth/login"
                      onClick={() => setMenuOpen(false)}
                      className="block px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50"
                    >
                      Sign In
                    </Link>
                  )}

                  <Link
                    to="/"
                    onClick={() => setMenuOpen(false)}
                    className="block px-4 py-2 text-sm text-gray-800 hover:bg-gray-50"
                  >
                    Portal Home
                  </Link>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </header>
  );
}

interface PublicPageLayoutProps {
  children: ReactNode;
  pageTitle?: string;
}

export function PublicPageLayout({ children }: PublicPageLayoutProps) {
  const { checkAuth } = useAuthStore();

  useEffect(() => {
    void checkAuth();
  }, [checkAuth]);

  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />
      <main>{children}</main>
    </div>
  );
}
